/** QuickPlan — Notice de montage (façon IKEA) : repères de pièces, perçages, étapes.
 *
 * computeNotice(project, lookup) rend :
 *   pieces   : types de pièces repérés A, B, C… (même composant, même désignation ET
 *              mêmes perçages : un côté gauche et un côté droit ont deux lettres),
 *              avec leurs perçages face par face, cotés depuis le coin bas-gauche de
 *              la face vue de l'extérieur ;
 *   hardware : quincaillerie totale (emplacements de fixation, kits, fixations posées) ;
 *   steps    : étapes dans l'ordre (instance.step, hérité du porteur si absent),
 *              avec pièces nouvelles, pièces déjà montées et quincaillerie de l'étape ;
 *   fixings  : emplacements de fixation en coordonnées monde (illustrations éclatées).
 *
 * Perçages : la tige de chaque fixation (de la tête vers la pointe, longueur l) est
 * coupée par les pièces « à percer ». Une pièce traversée reçoit un trou de passage,
 * celle où finit la pointe un avant-trou borgne (diamètres selon le type de fixation).
 * Quincaillerie dessinée sans emplacement déclaré (cylindres) : trou si le cylindre entre
 * dans la pièce, encoche s'il ne fait qu'affleurer une face.
 * Logements (encoches) : les pavés `recesses` d'un composant (palette de taquet…)
 * coupés par une pièce à percer donnent une encoche sur la face où ils sont le moins
 * profonds, cotée comme les trous, « débouchante » sur les chants qu'ils atteignent.
 * Les articulations sont fermées : la notice montre le meuble tel qu'on l'assemble.
 */
import { resolveComponent, partLocalBox, rotMatrix, apply, sub, add } from './resolve.js';
import { computeFrames, point, vector } from './frames.js';
import { fixingDesignation } from './bom.js';

const r1 = (v) => Math.round(v * 10) / 10;
const half = (v) => Math.round(v * 2) / 2;
const EPS = 1e-6;

/** Fixations posables à la main (shared/library/fixations.js) → type de fixation. */
const POSABLE = { 'qc-vis': 'vis', 'qc-clou': 'clou', 'qc-boulon': 'boulon', 'qc-cheville': 'cheville', 'qc-tourillon': 'tourillon', 'qc-rivet': 'rivet' };

/** Une pièce se perce-t-elle sur place ? Réglable par composant (`drill`). */
export function isDrillable(comp) {
  if (typeof comp.drill === 'boolean') return comp.drill;
  if (comp.category !== 'meuble' && comp.category !== 'autre') return false;
  if (comp.family === 'Quincaillerie' || comp.family === 'Fixations') return false;
  return (comp.bom?.kind ?? 'piece') !== 'none';
}

/** Diamètre de perçage selon la fixation et le rôle de la pièce (traversée ou pointe).
 *  null = pas de perçage (clou, agrafe : enfoncés directement). */
export function holeDiameter(type, d, through) {
  switch (type) {
    case 'clou': case 'agrafe': return null;
    case 'percage': return r1(d);
    case 'boulon': return half(d + 1);
    case 'rivet': return r1(d + 0.1);
    case 'tourillon': case 'cheville': case 'excentrique': return half(d);
    default: return through ? half(d) : Math.max(1.5, half(d * 0.7)); // vis : passage / avant-trou
  }
}

/** Faces d'une pièce vue de l'extérieur : axe normal, côté, axes u (→) et v (↑) avec leur sens. */
const FACES = {
  'x-': { k: 0, side: -1, u: [1, -1], v: [2, 1] },
  'x+': { k: 0, side: 1, u: [1, 1], v: [2, 1] },
  'y-': { k: 1, side: -1, u: [0, 1], v: [2, 1] },
  'y+': { k: 1, side: 1, u: [0, -1], v: [2, 1] },
  'z-': { k: 2, side: -1, u: [0, 1], v: [1, -1] },
  'z+': { k: 2, side: 1, u: [0, 1], v: [1, 1] },
};
const SIDE_NAMES = { Face: [['gauche', 'droite'], ['avant', 'arrière'], ['dessous', 'dessus']], Chant: [['gauche', 'droit'], ['avant', 'arrière'], ['bas', 'haut']] };
const FACE_ORDER = ['z+', 'z-', 'y-', 'y+', 'x-', 'x+'];

export function faceLabel(code, size) {
  const f = FACES[code];
  const kind = size[f.k] <= Math.min(...size) + EPS ? 'Face' : 'Chant';
  return `${kind} ${SIDE_NAMES[kind][f.k][f.side > 0 ? 1 : 0]}`;
}

/** Dimensions (largeur, hauteur) de la vue d'une face. */
/** Ce qui borde la vue d'une face : { left, right, bottom, top } → libellé du chant / de la face voisine. */
export function faceEdges(code, size) {
  const f = FACES[code];
  const side = ([ax, sgn], high) => `${'xyz'[ax]}${(sgn > 0) === high ? '+' : '-'}`;
  return {
    left: faceLabel(side(f.u, false), size),
    right: faceLabel(side(f.u, true), size),
    bottom: faceLabel(side(f.v, false), size),
    top: faceLabel(side(f.v, true), size),
  };
}

export function faceSize(code, size) {
  const f = FACES[code];
  return [size[f.u[0]], size[f.v[0]]];
}

/** Segment [a, b] ∩ boîte [lo, hi] : paramètres d'entrée / sortie et face d'entrée. */
function clipSegment(a, b, lo, hi) {
  const d = sub(b, a);
  let t0 = 0;
  let t1 = 1;
  let axis = -1;
  let side = 0;
  for (let k = 0; k < 3; k++) {
    if (Math.abs(d[k]) < EPS) {
      if (a[k] < lo[k] - EPS || a[k] > hi[k] + EPS) return null;
      continue;
    }
    let ta = (lo[k] - a[k]) / d[k];
    let tb = (hi[k] - a[k]) / d[k];
    let s = -1; // entrée par la face basse si on avance vers +k
    if (ta > tb) { [ta, tb] = [tb, ta]; s = 1; }
    if (ta > t0) { t0 = ta; axis = k; side = s; }
    if (tb < t1) t1 = tb;
    if (t0 > t1) return null;
  }
  if (axis < 0) { // départ déjà dans la boîte : face opposée à la direction d'avance
    axis = [0, 1, 2].reduce((m, k) => (Math.abs(d[k]) > Math.abs(d[m]) ? k : m), 0);
    side = d[axis] > 0 ? -1 : 1;
  }
  return { t0, t1, axis, side };
}

const isSlot = (p) => p.type === 'extrude' && typeof p.section === 'string' && p.section.startsWith('slot');

/** Perçages d'une fixation (repère composant de la pièce : a = tête, b = pointe). */
function holesInPiece(res, a, b, fix) {
  const out = [];
  const len = Math.hypot(...sub(b, a));
  if (len < EPS) return out;
  // Tête prolongée de 0,5 mm vers l'extérieur : une tête posée SUR la face entre bien par elle.
  const dir = sub(b, a).map((v) => v / len);
  const a2 = sub(a, dir.map((v) => v * 0.5));
  const L = len + 0.5;
  for (const p of res.parts) {
    if (isSlot(p)) continue;
    const R = p.rot ? rotMatrix(p.rot) : null;
    const Rt = R && [0, 1, 2].map((i) => [R[0][i], R[1][i], R[2][i]]);
    const toPart = (x) => (Rt ? apply(Rt, sub(x, p.pos)) : sub(x, p.pos));
    const [lo, hi] = partLocalBox(p);
    const c = clipSegment(toPart(a2), toPart(b), lo, hi);
    if (!c) continue;
    const depth = (c.t1 - c.t0) * L;
    if (depth < 0.3) continue;
    const through = c.t1 < 1 - EPS;
    const dia = holeDiameter(fix.type, fix.d, through);
    if (dia == null) continue;
    // Normale de la face d'entrée, ramenée au repère composant → face de la boîte englobante.
    const nLocal = [0, 0, 0];
    nLocal[c.axis] = c.side;
    const n = R ? apply(R, nLocal) : nLocal;
    const k = [0, 1, 2].reduce((m, i) => (Math.abs(n[i]) > Math.abs(n[m]) ? i : m), 0);
    const entry = add(a2, dir.map((v) => v * c.t0 * L));
    out.push({ face: `${'xyz'[k]}${n[k] > 0 ? '+' : '-'}`, entry, dia, depth: r1(depth), through, type: fix.type, what: fix.what });
  }
  return out;
}

/** Coordonnées (u, v) d'un point sur la vue d'une face, depuis son coin bas-gauche. */
function faceCoords(code, bbox, p) {
  const f = FACES[code];
  const along = ([ax, s]) => (s > 0 ? p[ax] - bbox.min[ax] : bbox.max[ax] - p[ax]);
  return [r1(along(f.u)), r1(along(f.v))];
}

function letterOf(i) {
  let s = '';
  let n = i;
  do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
  return s;
}

export function computeNotice(project, lookup) {
  const errors = [];
  // Articulations fermées : on monte portes et tiroirs fermés.
  const instances = (project.instances ?? []).map((i) => ({ ...i, open: 0 }));
  const byId = new Map(instances.map((i) => [i.id, i]));
  const resById = new Map();
  const compById = new Map();
  for (const inst of instances) {
    const comp = lookup(inst.componentId);
    if (!comp) { errors.push(`${inst.name || inst.id} : composant « ${inst.componentId} » introuvable`); continue; }
    try {
      resById.set(inst.id, resolveComponent(comp, inst.params));
      compById.set(inst.id, comp);
    } catch (e) {
      errors.push(`${inst.name || inst.id} : ${e.message}`);
    }
  }
  const F = computeFrames({ ...project, instances }, (i) => resById.get(i.id) ?? null);

  // ── Étape effective : la sienne, sinon celle de son porteur ──
  const stepOf = new Map();
  const effStep = (inst, guard = 0) => {
    if (stepOf.has(inst.id)) return stepOf.get(inst.id);
    let s = inst.step ?? null;
    if (s == null && inst.parentId && byId.has(inst.parentId) && guard < 100) s = effStep(byId.get(inst.parentId), guard + 1);
    stepOf.set(inst.id, s);
    return s;
  };
  for (const i of instances) effStep(i);

  // ── Fixations en coordonnées monde ──
  const fixings = [];
  for (const inst of instances) {
    const res = resById.get(inst.id);
    if (!res) continue;
    const f = F.get(inst.id);
    for (const x of res.fixings) {
      const des = fixingDesignation(x);
      const what = x.type === 'percage' ? compById.get(inst.id).name : des.name;
      fixings.push({ instId: inst.id, type: x.type, d: x.d, l: x.l, pos: point(f, x.pos), dir: vector(f, x.dir), ref: des.ref, name: des.name, what });
    }
    const type = POSABLE[inst.componentId];
    if (type) {
      const D = res.params.D ?? 4;
      const Lp = res.params.L ?? 20;
      const head = type === 'tourillon' ? [0, 0, Lp / 2] : [0, 0, 0];
      fixings.push({ instId: inst.id, posable: true, type, d: D, l: Lp, pos: point(f, head), dir: vector(f, [0, 0, 1]), what: `${compById.get(inst.id).name} ${res.label ?? ''}`.trim() });
    }
  }

  // ── Perçages : chaque tige contre chaque pièce à percer (préfiltre par boîtes monde) ──
  const drillable = instances.filter((i) => resById.has(i.id) && isDrillable(compById.get(i.id)));
  const worldBox = new Map();
  for (const inst of drillable) {
    const res = resById.get(inst.id);
    const f = F.get(inst.id);
    const lo = [Infinity, Infinity, Infinity];
    const hi = [-Infinity, -Infinity, -Infinity];
    for (const x of [res.bbox.min[0], res.bbox.max[0]]) for (const y of [res.bbox.min[1], res.bbox.max[1]]) for (const z of [res.bbox.min[2], res.bbox.max[2]]) {
      const p = point(f, [x, y, z]);
      for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], p[k]); hi[k] = Math.max(hi[k], p[k]); }
    }
    worldBox.set(inst.id, { lo, hi });
  }
  const holesOf = new Map(); // instId → [{ face, u, v, dia, depth, through, type, what }]
  const recessOf = new Map(); // instId → [{ face, u, v, w, h, depth, open, name }]
  /** Repère monde → repère composant d'une pièce (R orthonormée : inverse = transposée). */
  const toLocalOf = (inst) => {
    const f = F.get(inst.id);
    const Rt = [0, 1, 2].map((i) => [f.R[0][i], f.R[1][i], f.R[2][i]]);
    return (p) => apply(Rt, sub(p, f.t));
  };
  const outside = (inst, lo, hi, gap = 0) => {
    const wb = worldBox.get(inst.id);
    return [0, 1, 2].some((k) => wb.hi[k] < lo[k] - gap || wb.lo[k] > hi[k] + gap);
  };
  /** Trous d'une tige a (tête) → b (pointe), coordonnées pièce. */
  const addHoles = (inst, res, a, b, fx) => {
    for (const h of holesInPiece(res, a, b, fx)) {
      const [u, v] = faceCoords(h.face, res.bbox, h.entry);
      const list = holesOf.get(inst.id) ?? [];
      if (!list.some((o) => o.face === h.face && Math.abs(o.u - u) < 0.2 && Math.abs(o.v - v) < 0.2)) {
        list.push({ face: h.face, u, v, dia: h.dia, depth: h.depth, through: h.through, type: h.type, what: h.what });
      }
      holesOf.set(inst.id, list);
    }
  };
  /** Encoche [lo, hi] (coordonnées pièce, déjà bornée à un pavé de la pièce) : usinée depuis
   *  la face où elle est le moins profonde, « débouchante » sur les autres faces atteintes. */
  const addRecess = (inst, res, lo, hi, name) => {
    if ([0, 1, 2].some((k) => hi[k] - lo[k] < 0.2)) return;
    const touched = [];
    for (let k = 0; k < 3; k++) {
      if (Math.abs(lo[k] - res.bbox.min[k]) < 0.3) touched.push({ code: `${'xyz'[k]}-`, k, depth: hi[k] - lo[k] });
      if (Math.abs(hi[k] - res.bbox.max[k]) < 0.3) touched.push({ code: `${'xyz'[k]}+`, k, depth: hi[k] - lo[k] });
    }
    if (!touched.length) return; // enfermé dans la matière : pas usinable
    touched.sort((x, y) => x.depth - y.depth);
    const main = touched[0];
    const size = [0, 1, 2].map((k) => res.bbox.max[k] - res.bbox.min[k]);
    const [u0, v0] = faceCoords(main.code, res.bbox, lo);
    const [u1, v1] = faceCoords(main.code, res.bbox, hi);
    const rec = {
      face: main.code,
      u: Math.min(u0, u1), v: Math.min(v0, v1), w: r1(Math.abs(u1 - u0)), h: r1(Math.abs(v1 - v0)),
      depth: r1(main.depth),
      open: touched.slice(1).filter((t) => t.k !== main.k).map((t) => faceLabel(t.code, size)),
      name,
    };
    const list = recessOf.get(inst.id) ?? [];
    if (!list.some((o) => o.face === rec.face && Math.abs(o.u - rec.u) < 0.2 && Math.abs(o.v - rec.v) < 0.2)) list.push(rec);
    recessOf.set(inst.id, list);
  };
  const boxParts = (res) => res.parts.filter((pt) => pt.type === 'box' && !pt.rot); // panneaux droits

  // ── Perçages des emplacements de fixation : chaque tige contre chaque pièce à percer ──
  for (const fx of fixings) {
    const a = fx.pos;
    const b = sub(fx.pos, fx.dir.map((v) => v * fx.l));
    const lo = [0, 1, 2].map((k) => Math.min(a[k], b[k]));
    const hi = [0, 1, 2].map((k) => Math.max(a[k], b[k]));
    for (const inst of drillable) {
      if ((inst.id === fx.instId && fx.posable) || outside(inst, lo, hi, 1)) continue;
      const toLocal = toLocalOf(inst);
      addHoles(inst, resById.get(inst.id), toLocal(a), toLocal(b), fx);
    }
  }

  // ── Logements déclarés (recesses) : pavés des composants coupés par les pièces à percer ──
  for (const carrier of instances) {
    const cres = resById.get(carrier.id);
    if (!cres?.recesses?.length) continue;
    const cf = F.get(carrier.id);
    for (const rc of cres.recesses) {
      const corners = [];
      for (const x of [rc.min[0], rc.max[0]]) for (const y of [rc.min[1], rc.max[1]]) for (const z of [rc.min[2], rc.max[2]]) corners.push(point(cf, [x, y, z]));
      const wlo = [0, 1, 2].map((k) => Math.min(...corners.map((c) => c[k])));
      const whi = [0, 1, 2].map((k) => Math.max(...corners.map((c) => c[k])));
      for (const inst of drillable) {
        if (inst.id === carrier.id || outside(inst, wlo, whi)) continue;
        const toLocal = toLocalOf(inst);
        const loc = corners.map(toLocal);
        const res = resById.get(inst.id);
        for (const part of boxParts(res)) {
          const lo = [0, 1, 2].map((k) => Math.max(part.pos[k], Math.min(...loc.map((c) => c[k]))));
          const hi = [0, 1, 2].map((k) => Math.min(part.pos[k] + part.size[k], Math.max(...loc.map((c) => c[k]))));
          addRecess(inst, res, lo, hi, rc.name);
        }
      }
    }
  }

  // ── Quincaillerie dessinée sans emplacement (taquet, cheville, ligne de trous… créés
  //    par l'assistant en simples cylindres) : un cylindre qui ENTRE dans une pièce à percer
  //    demande un trou de son diamètre ; un cylindre qui ne fait qu'affleurer une face (une
  //    partie seulement de son diamètre dans la matière) demande une encoche. ──
  for (const carrier of instances) {
    const comp = compById.get(carrier.id);
    const cres = resById.get(carrier.id);
    if (!cres || comp.category !== 'quincaillerie' || POSABLE[carrier.componentId]) continue;
    if (cres.fixings.length || cres.recesses?.length) continue; // perçages déjà déclarés
    const cf = F.get(carrier.id);
    for (const part of cres.parts) {
      if (part.type !== 'cyl') continue;
      const R = part.rot ? rotMatrix(part.rot) : null;
      const axisLocal = { x: [1, 0, 0], y: [0, 1, 0], z: [0, 0, 1] }[part.axis ?? 'z'];
      const dirC = R ? apply(R, axisLocal) : axisLocal;
      const A = point(cf, part.pos);
      const B = point(cf, add(part.pos, dirC.map((v) => v * part.h)));
      const r = part.r;
      const lo = [0, 1, 2].map((k) => Math.min(A[k], B[k]) - r);
      const hi = [0, 1, 2].map((k) => Math.max(A[k], B[k]) + r);
      const what = comp.name;
      for (const inst of drillable) {
        if (outside(inst, lo, hi)) continue;
        const toLocal = toLocalOf(inst);
        const a = toLocal(A);
        const b = toLocal(B);
        const d = sub(b, a);
        const k = [0, 1, 2].reduce((m, i) => (Math.abs(d[i]) > Math.abs(d[m]) ? i : m), 0); // axe du cylindre
        const res = resById.get(inst.id);
        for (const pt of boxParts(res)) {
          const plo = pt.pos;
          const phi = add(pt.pos, pt.size);
          const clo = [0, 1, 2].map((i) => Math.max(plo[i], Math.min(a[i], b[i]) - (i === k ? 0 : r)));
          const chi = [0, 1, 2].map((i) => Math.min(phi[i], Math.max(a[i], b[i]) + (i === k ? 0 : r)));
          if ([0, 1, 2].some((i) => chi[i] - clo[i] < 0.2)) continue;
          const full = [0, 1, 2].every((i) => i === k || chi[i] - clo[i] >= 2 * r - 0.2);
          if (!full) { addRecess(inst, res, clo, chi, `logement (${what})`); continue; }
          // Trou : on perce depuis l'extrémité qui sort de la pièce.
          const inA = a[k] > plo[k] + 0.3 && a[k] < phi[k] - 0.3;
          const inB = b[k] > plo[k] + 0.3 && b[k] < phi[k] - 0.3;
          if (inA && inB) continue; // enfermé dans la matière
          const fx = { type: 'percage', d: 2 * r, what };
          if (inA) addHoles(inst, res, b, a, fx); else addHoles(inst, res, a, b, fx);
        }
      }
    }
  }

  // ── Types de pièces (lettres) : quincaillerie à part ──
  const isHardware = (inst) => compById.get(inst.id)?.category === 'quincaillerie';
  const types = new Map();
  for (const inst of instances) {
    const res = resById.get(inst.id);
    if (!res || isHardware(inst)) continue;
    const comp = compById.get(inst.id);
    const holes = (holesOf.get(inst.id) ?? []).sort((p, q) => FACE_ORDER.indexOf(p.face) - FACE_ORDER.indexOf(q.face) || p.u - q.u || p.v - q.v);
    const designation = res.length != null ? `${String(res.length).replace('.', ',')} mm` : res.label;
    const recesses = (recessOf.get(inst.id) ?? []).sort((p, q) => FACE_ORDER.indexOf(p.face) - FACE_ORDER.indexOf(q.face) || p.u - q.u || p.v - q.v);
    const sig = [...holes.map((h) => `${h.face}:${h.u}:${h.v}:${h.dia}:${h.depth}`), ...recesses.map((r) => `R${r.face}:${r.u}:${r.v}:${r.w}:${r.h}:${r.depth}`)].join('|');
    const key = `${comp.id}|${designation}|${sig}`;
    let t = types.get(key);
    if (!t) {
      const size = [0, 1, 2].map((k) => r1(res.bbox.max[k] - res.bbox.min[k]));
      const faces = FACE_ORDER.filter((c) => holes.some((h) => h.face === c) || recesses.some((r) => r.face === c)).map((c) => ({
        code: c, label: faceLabel(c, size), size: faceSize(c, size), holes: holes.filter((h) => h.face === c), recesses: recesses.filter((r) => r.face === c),
      }));
      t = { key, componentId: comp.id, name: comp.name, label: designation, material: comp.material, category: comp.category, size, faces, holeCount: holes.length, recessCount: recesses.length, qty: 0, instances: [], firstStep: Infinity, sampleId: inst.id };
      types.set(key, t);
    }
    t.qty++;
    t.instances.push(inst.id);
    t.firstStep = Math.min(t.firstStep, stepOf.get(inst.id) ?? Infinity);
  }
  const vol = (t) => t.size[0] * t.size[1] * t.size[2];
  const pieces = [...types.values()].sort((a, b) => a.firstStep - b.firstStep || vol(b) - vol(a) || a.name.localeCompare(b.name, 'fr'));
  pieces.forEach((t, i) => { t.letter = letterOf(i); });
  const letterOfInst = new Map();
  for (const t of pieces) for (const id of t.instances) letterOfInst.set(id, t.letter);

  // ── Quincaillerie d'un ensemble d'instances ──
  const hardwareOf = (ids) => {
    const map = new Map();
    const bump = (ref, name, qty) => {
      const row = map.get(ref) ?? { ref, name, qty: 0 };
      row.qty += qty;
      map.set(ref, row);
    };
    for (const id of ids) {
      const res = resById.get(id);
      if (!res) continue;
      for (const x of res.fixings) {
        if (x.type === 'percage') continue;
        const d = fixingDesignation(x);
        bump(d.ref, d.name, 1);
      }
      for (const k of res.kit) bump(k.ref, k.name, k.qty);
      const inst = byId.get(id);
      if (isHardware(inst) && compById.get(id).bom?.kind !== 'none') {
        const comp = compById.get(id);
        bump(`${comp.ref || comp.id}${res.label ? ` ${res.label}` : ''}`, `${comp.name}${res.label ? ` ${res.label}` : ''}`, 1);
      }
    }
    return [...map.values()].map((r) => ({ ...r, qty: Math.round(r.qty * 100) / 100 })).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  };

  // ── Étapes ──
  const valid = instances.filter((i) => resById.has(i.id));
  const numbers = [...new Set(valid.map((i) => stepOf.get(i.id)).filter((s) => s != null))].sort((a, b) => a - b);
  const unassigned = valid.filter((i) => stepOf.get(i.id) == null);
  const groups = numbers.map((n) => ({ n, ids: valid.filter((i) => stepOf.get(i.id) === n).map((i) => i.id), unassigned: false }));
  if (unassigned.length) groups.push({ n: null, ids: unassigned.map((i) => i.id), unassigned: true });

  const steps = [];
  const done = [];
  groups.forEach((g, k) => {
    const counts = new Map();
    for (const id of g.ids) {
      const l = letterOfInst.get(id);
      if (l) counts.set(l, (counts.get(l) ?? 0) + 1);
    }
    const piecesOfStep = [...counts.entries()].map(([letter, qty]) => ({ letter, qty })).sort((a, b) => a.letter.length - b.letter.length || a.letter.localeCompare(b.letter));
    const notes = g.ids.map((id) => byId.get(id)).filter((i) => i.note).map((i) => ({ name: i.name || compById.get(i.id)?.name || i.id, note: i.note }));
    steps.push({
      index: k + 1,
      n: g.n,
      unassigned: g.unassigned,
      newIds: g.ids,
      prevIds: [...done],
      pieces: piecesOfStep,
      hardware: hardwareOf(g.ids),
      notes,
    });
    done.push(...g.ids);
  });

  const allIds = valid.map((i) => i.id);
  return {
    pieces,
    letterOf: Object.fromEntries(letterOfInst),
    hardware: hardwareOf(allIds),
    steps,
    fixings,
    holeCount: pieces.reduce((s, t) => s + t.holeCount * t.qty, 0),
    recessCount: pieces.reduce((s, t) => s + t.recessCount * t.qty, 0),
    noSteps: numbers.length === 0,
    errors,
  };
}
